import AppService from 'App/legacy/AppService';
import Metadata from 'App/babel/resources/Metadata';
import CustomField from 'App/babel/resources/CustomField';
import EventBus from 'App/services/EventBus';
import logError from 'App/babel/helpers/logError';

/*
	Standalone CacheRefresher (WEB-7780). Covers the METADATA cache type and the eighteen
	custom-field cache types — every remaining type still needs one of the Angular-only $resource
	factories, so the plumbing stays in upsales/common/services/cacheRefresher.js for now (it is
	also still injected by DI into upsales/common/resources/pushNotifications.js).

	This module POSTS only. The apply side (`AppService.setMetadata` / `AppService.setCustomFields`)
	remains cacheRefresher.js's `listenChannel.onmessage`: a BroadcastChannel never receives its own
	posts, but every OTHER channel object on the same name does — including ones in this tab. So the
	messages below reach Angular's listener here and in every other tab, which is the same flow as
	today. Adding a second listener while both implementations coexist would dispatch setMetadata /
	setCustomFields twice.

	It also does not post `{ msg: 'cancelDebounce' }`. Angular posts that on its listenChannel, where
	it is same-tab inert and only cancels other tabs' pending debounce. Posting it from a new channel
	object here would newly be delivered to Angular's listener in THIS tab and cancel a pending
	push-driven refresh of an unrelated cache type. Omitting it costs at most a redundant GET in
	other tabs.

	While both implementations coexist there are two independent debouncers per cache type: server
	pushes still route through Angular (pushNotifications.js -> CacheRefresher.refresh(data.types)),
	and neither timer can cancel the other. Worst case that is one extra GET plus one extra
	broadcast, which lands on the same setter. It resolves itself when the remaining cache types are
	ported and cacheRefresher.js goes away.

	DELIBERATE DIVERGENCE — per-cacheType timers. Angular has a SINGLE shared `_.debounce(refresh)`,
	so `refresh(['Metadata'])` followed within the 10–30s window by `refresh(['ClientCustomField'])`
	silently DROPS the metadata refresh: the second call restarts the one timer with new arguments.
	The map below keys the timer by cache type, so both refreshes survive. This is an improvement,
	not a reimplementation slip — replicating the drop would mean deliberately losing a refresh the
	caller asked for.
*/

// Add random delay between 10 and 30 seconds. Angular computes this once per service instance;
// module scope is the equivalent.
const debounceTime = 10_000 + Math.random() * 20_000;

const broadcastChannel = new BroadcastChannel('cache_refresh_channel');

// Must match cacheTypes in upsales/common/services/cacheRefresher.js — that switch is what turns a
// message back into an AppService.setMetadata / AppService.setCustomFields call.
const METADATA = 'Metadata';

export type CustomFieldCacheType =
	| 'ActivityCustomField'
	| 'TodoCustomField'
	| 'AgreementCustomField'
	| 'AppointmentCustomField'
	| 'ClientCustomField'
	| 'ContactCustomField'
	| 'LeadCustomField'
	| 'OrderCustomField'
	| 'OrderedProductCustomField'
	| 'ProductCustomField'
	| 'ProjectCustomField'
	| 'ProjectPlanCustomField'
	| 'TicketCustomField'
	| 'UserCustomField'
	| 'UserDefinedObjectCustomField'
	| 'UserDefinedObject2CustomField'
	| 'UserDefinedObject3CustomField'
	| 'UserDefinedObject4CustomField';

// Copied verbatim from cacheRefresher.js's typeMap. The string form is the customfields url
// segment; the object form carries the userDefinedObject number the udo endpoint takes.
const typeMap: { [cacheType: string]: string | { type: string; number: number } } = {
	ActivityCustomField: 'activity',
	TodoCustomField: 'todo',
	AgreementCustomField: 'agreement',
	AppointmentCustomField: 'appointment',
	ClientCustomField: 'account',
	ContactCustomField: 'contact',
	LeadCustomField: 'lead',
	OrderCustomField: 'order',
	OrderedProductCustomField: 'orderrow',
	ProductCustomField: 'product',
	ProjectCustomField: 'project',
	ProjectPlanCustomField: 'projectPlan',
	TicketCustomField: 'ticket',
	UserCustomField: 'user',
	UserDefinedObjectCustomField: { type: 'userDefined1', number: 1 },
	UserDefinedObject2CustomField: { type: 'userDefined2', number: 2 },
	UserDefinedObject3CustomField: { type: 'userDefined3', number: 3 },
	UserDefinedObject4CustomField: { type: 'userDefined4', number: 4 }
};

const timers = new Map<string, ReturnType<typeof setTimeout>>();

/*
	Trailing debounce, hand-rolled rather than _.debounce: some suites jest.mock('lodash') and this
	module is reached transitively from several of them.

	The cache keeps its previous value until the next refresh, so there is nothing to recover in the
	catch — but the rejection still needs a handler. Angular's $q swallowed unhandled rejections; a
	native one would surface as `Uncaught (in promise) 403` and reach Sentry as a non-Error
	rejection, since Metadata.get() rejects with a bare status code.
*/
const schedule = (cacheType: string, run: (customerId: number) => Promise<void>): void => {
	clearTimeout(timers.get(cacheType));
	timers.set(
		cacheType,
		setTimeout(() => {
			timers.delete(cacheType);
			run(AppService.getCustomerId()).catch(err => logError(err, `CacheRefresher: ${cacheType} refresh failed`));
		}, debounceTime)
	);
};

/*
	Returns undefined, like Angular's refreshProxy does on its debounced path — which is why the
	`await` at ChooseMailIntegrationReducer.js is already a no-op. Preserved deliberately.
*/
export const refreshMetadata = (): void =>
	schedule(METADATA, customerId =>
		Metadata.get().then(data => broadcastChannel.postMessage({ cacheType: METADATA, data, customerId }))
	);

export const refreshCustomFields = (cacheType: CustomFieldCacheType): void =>
	schedule(cacheType, customerId => {
		const mapped = typeMap[cacheType];

		/*
			Reachable, not defensive: the route is /admin/fields/:type?, so an arbitrary url segment
			reaches AdminFields' cfTypesMap lookup as undefined, and 'opportunity' is a real customFields
			key with no cfTypesMap entry. Angular logged this exact message from its switch's default
			branch and then threw a TypeError on `undefined.then` in the promises.forEach below it. The
			log is reproduced; the crash is not.
		*/
		if (!mapped) {
			logError(cacheType, 'CacheRefresher.refresh - Unknown cache type');
			return Promise.resolve();
		}

		if (typeof mapped === 'string') {
			return CustomField.findByType(mapped).then(data => {
				EventBus.broadcast('cacheRefresher.customField', mapped);
				broadcastChannel.postMessage({ cacheType, type: mapped, data, customerId });
			});
		}

		return CustomField.findUdo(mapped.number).then(data => {
			EventBus.broadcast('cacheRefresher.userDefinedObjectCustomField', mapped.type);
			broadcastChannel.postMessage({ cacheType, type: mapped.type, data, customerId });
		});
	});
